import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  MessageSquare, 
  RefreshCw, 
  Clock, 
  CheckCircle, 
  AlertTriangle,
  Send,
  FileText,
  ExternalLink,
  ChevronDown,
  ChevronUp,
  Mail,
  User,
  Building,
  Calendar,
  RotateCcw,
  TrendingUp,
  Power,
  PowerOff,
  Pause,
  Play,
  XCircle,
  ShieldAlert,
  ThumbsUp,
  ThumbsDown,
  Eye,
  Reply,
  CalendarClock,
  Edit3,
  Download,
  Inbox,
  Link,
  ChevronLeft,
  ChevronRight,
  Image
} from 'lucide-react';
import { toast } from 'sonner';
import ServiceLLMToggle from '../components/ServiceLLMToggle';
import { toLocalInput, toUtc } from '../lib/interviewDates';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper pour gérer les erreurs réseau de manière consistante
const handleNetworkError = (e, context = 'opération') => {
  console.error(`[Interviews] Network error during ${context}:`, e);
  toast.error(`Erreur réseau: ${e.message || 'connexion impossible'}`);
};

const STATUS_CONFIG = {
  new: { label: 'Nouveau', color: 'bg-blue-500', icon: Mail },
  questions_sent: { label: 'Questions envoyées', color: 'bg-yellow-500', icon: Send },
  response_pending: { label: 'En attente', color: 'bg-orange-500', icon: Clock },
  response_received: { label: 'Réponse reçue', color: 'bg-green-500', icon: CheckCircle },
  needs_human_review: { label: '⚠️ Arbitrage requis', color: 'bg-amber-600', icon: ShieldAlert },
  article_generated: { label: 'Article généré', color: 'bg-purple-500', icon: FileText },
  pending_review: { label: 'À valider', color: 'bg-sky-500', icon: AlertTriangle },
  published: { label: 'Publié', color: 'bg-emerald-500', icon: CheckCircle },
  redirected: { label: 'Redirigé partenariats', color: 'bg-gray-500', icon: ExternalLink },
  abandoned: { label: 'Abandonné', color: 'bg-red-700', icon: AlertTriangle },
  error: { label: 'Erreur', color: 'bg-red-500', icon: AlertTriangle },
};

// Composant pour les interviews en attente d'arbitrage
function ArbitrageCard({ interview, token, onRefresh }) {
  const [loading, setLoading] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [showResponseSelector, setShowResponseSelector] = useState(false);
  const [selectedResponses, setSelectedResponses] = useState([]);
  const [selectedPortrait, setSelectedPortrait] = useState(null);

  const validation = interview.validation_result || {};
  const redFlags = validation.red_flags || [];
  const score = validation.confidence_score || 0;
  const reason = interview.review_reason || validation.rejection_reason || 'Contenu non conforme';
  const responses = interview.responses || [];

  // Extraire toutes les images disponibles dans les réponses
  const availableImages = [];
  responses.forEach((response, respIdx) => {
    (response.saved_attachments || []).forEach((att, attIdx) => {
      if (att.content_type?.startsWith('image/')) {
        availableImages.push({
          responseIndex: respIdx,
          attachmentIndex: attIdx,
          filename: att.filename || 'image',
          path: att.saved_path,
          contentType: att.content_type,
          key: `${respIdx}-${attIdx}`
        });
      }
    });
  });

  // Sous-type : suggestion d'interview détectée (vs validation post-réponse)
  const isSuggestion = interview.review_reason === 'not_interview_proposal' && !!interview.suggested_interview;
  const suggestion = interview.suggested_interview || {};
  const suggestedQuestions = suggestion.suggested_questions || interview.questions || [];

  // Initialiser la sélection avec les réponses contenant des "answers" et la première image
  useEffect(() => {
    if (responses.length > 0 && selectedResponses.length === 0) {
      const answersIndexes = responses
        .map((r, i) => ({ index: i, analysis: r.analysis || {} }))
        .filter(r => r.analysis.response_type === 'answers' || r.analysis.has_answers)
        .map(r => r.index);
      setSelectedResponses(answersIndexes.length > 0 ? answersIndexes : [0]);
    }
    // Auto-sélectionner la première image si disponible
    if (availableImages.length > 0 && !selectedPortrait) {
      setSelectedPortrait(availableImages[0].key);
    }
  }, [responses, selectedResponses.length, availableImages.length, selectedPortrait]);

  const toggleResponseSelection = (index) => {
    setSelectedResponses(prev => 
      prev.includes(index) 
        ? prev.filter(i => i !== index)
        : [...prev, index].sort((a, b) => a - b)
    );
  };

  const handleApprove = async () => {
    if (isSuggestion) {
      // Approbation d'une SUGGESTION : envoie les questions suggérées à l'expéditeur
      const recipient = interview.original_sender?.email || '?';
      if (!window.confirm(`Envoyer les ${suggestedQuestions.length} questions suggérées à ${recipient} ?\n\nL'interview démarrera sur la base de l'angle proposé par l'IA.`)) return;
      setLoading(true);
      try {
        const res = await fetch(`${API_URL}/api/interviews/${interview.id}/approve-suggested-interview`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          toast.success(`Questions envoyées à ${recipient}`);
          onRefresh();
        } else {
          const data = await res.json();
          toast.error(data.detail || 'Erreur lors de l\'envoi');
        }
      } catch (e) {
        handleNetworkError(e, 'envoi questions');
      }
      setLoading(false);
      return;
    }

    // Cas standard : validation post-réponse → forcer la génération de l'article
    // Si plusieurs réponses ou images, proposer la sélection
    if ((responses.length > 1 || availableImages.length > 1) && !showResponseSelector) {
      setShowResponseSelector(true);
      return;
    }

    if (selectedResponses.length === 0) {
      toast.error('Sélectionnez au moins une réponse à utiliser');
      return;
    }

    // Préparer les données du portrait sélectionné
    let portraitData = null;
    if (selectedPortrait) {
      const portrait = availableImages.find(img => img.key === selectedPortrait);
      if (portrait) {
        portraitData = {
          response_index: portrait.responseIndex,
          attachment_index: portrait.attachmentIndex,
          path: portrait.path
        };
      }
    }

    const confirmMsg = availableImages.length > 0 
      ? `Générer l'article avec ${selectedResponses.length} réponse(s) et ${selectedPortrait ? 'le portrait sélectionné' : 'aucun portrait'} ?\n\nVous confirmez que le contenu est une interview valide.`
      : `Générer l'article avec ${selectedResponses.length} réponse(s) sélectionnée(s) ?\n\nVous confirmez que le contenu est une interview valide.`;
    
    if (!window.confirm(confirmMsg)) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/interviews/${interview.id}/approve-for-generation`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          selected_responses: selectedResponses,
          selected_portrait: portraitData
        })
      });
      if (res.ok) {
        toast.success('Interview approuvée — génération de l\'article lancée');
        setShowResponseSelector(false);
        onRefresh();
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Erreur lors de l\'approbation');
      }
    } catch (e) {
      handleNetworkError(e, 'approbation');
    }
    setLoading(false);
  };
  
  const handleReject = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/interviews/${interview.id}/reject-as-non-interview`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: reason })
      });
      if (res.ok) {
        toast.success('Interview rejetée');
        onRefresh();
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Erreur lors du rejet');
      }
    } catch (e) {
      handleNetworkError(e, 'approbation');
    }
    setLoading(false);
  };
  
  // Extraire un aperçu des réponses
  const responsesPreview = (interview.responses || [])
    .map(r => r.body || '')
    .join('\n')
    .slice(0, 500);
  
  return (
    <div className="bg-slate-800/60 border border-amber-700/40 rounded-lg p-4" data-testid={`arbitrage-card-${interview.id}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          {/* Header */}
          <div className="flex items-center gap-2 mb-2">
            <ShieldAlert className="w-5 h-5 text-amber-400 flex-shrink-0" />
            <h3 className="font-medium text-white truncate">{interview.subject || 'Sans sujet'}</h3>
          </div>
          
          {/* Infos */}
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-400 mb-3">
            <span className="flex items-center gap-1">
              <User className="w-3.5 h-3.5" />
              {interview.analysis?.person_to_interview || 'Inconnu'}
            </span>
            <span className="flex items-center gap-1">
              <Building className="w-3.5 h-3.5" />
              {interview.analysis?.company || 'N/A'}
            </span>
            <span className="flex items-center gap-1">
              <Calendar className="w-3.5 h-3.5" />
              {interview.created_at ? new Date(interview.created_at).toLocaleDateString('fr-FR') : '?'}
            </span>
          </div>
          
          {/* Raison du blocage / type d'arbitrage */}
          <div className={`${isSuggestion ? 'bg-indigo-900/30 border-indigo-700/40' : 'bg-amber-900/30 border-amber-700/40'} border rounded p-3 mb-3`} data-testid={`arbitrage-reason-${interview.id}`}>
            <p className={`${isSuggestion ? 'text-indigo-200' : 'text-amber-200'} text-sm font-medium mb-1`}>
              {isSuggestion ? '💡 Suggestion d\'interview détectée' : 'Raison du blocage :'}
            </p>
            <p className={`${isSuggestion ? 'text-indigo-100/80' : 'text-amber-100/80'} text-sm`}>
              {isSuggestion
                ? "L'email reçu n'est pas une vraie proposition d'interview, mais l'IA a identifié un angle journalistique potentiel."
                : reason}
            </p>

            {/* Pitch + détails de la suggestion */}
            {isSuggestion && (
              <div className="mt-3 space-y-2 text-sm">
                {suggestion.angle_pitch && (
                  <div className="bg-slate-900/50 border border-indigo-700/30 rounded p-2">
                    <p className="text-xs text-indigo-300 mb-1 font-medium">Pitch journalistique :</p>
                    <p className="text-slate-200 italic">{suggestion.angle_pitch}</p>
                  </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
                  <div><span className="text-indigo-300">Sujet : </span><span className="text-slate-200">{suggestion.topic || '—'}</span></div>
                  <div><span className="text-indigo-300">Expert : </span><span className="text-slate-200">{suggestion.person_to_interview || '—'}</span></div>
                  <div><span className="text-indigo-300">Entreprise : </span><span className="text-slate-200">{suggestion.company || '—'}</span></div>
                  <div><span className="text-indigo-300">Questions : </span><span className="text-slate-200">{suggestedQuestions.length}</span></div>
                </div>
                {suggestedQuestions.length > 0 && (
                  <details className="mt-2">
                    <summary className="text-xs text-indigo-300 cursor-pointer hover:text-indigo-200">Voir les {suggestedQuestions.length} questions suggérées</summary>
                    <ol className="list-decimal list-inside mt-2 space-y-1 text-xs text-slate-300 pl-2">
                      {suggestedQuestions.map((q, i) => <li key={`sq-${i}-${q.slice(0, 20)}`}>{q}</li>)}
                    </ol>
                  </details>
                )}
              </div>
            )}

            {/* Score de confiance (validation post-réponse uniquement) */}
            {!isSuggestion && (
              <div className="flex items-center gap-2 mt-2">
                <span className="text-xs text-slate-400">Score de confiance :</span>
                <div className="flex-1 bg-slate-700 rounded-full h-2 max-w-[150px]">
                  <div
                    className={`h-2 rounded-full ${score >= 70 ? 'bg-green-500' : score >= 40 ? 'bg-amber-500' : 'bg-red-500'}`}
                    style={{ width: `${score}%` }}
                  />
                </div>
                <span className={`text-xs font-medium ${score >= 70 ? 'text-green-400' : score >= 40 ? 'text-amber-400' : 'text-red-400'}`}>
                  {score}/100
                </span>
              </div>
            )}
            
            {/* Red flags */}
            {redFlags.length > 0 && (
              <div className="mt-2">
                <p className="text-xs text-slate-400 mb-1">Signaux d&apos;alerte :</p>
                <div className="flex flex-wrap gap-1">
                  {redFlags.map((flag, i) => (
                    <Badge key={`rf-${i}-${flag.slice(0, 15)}`} className="bg-red-900/40 text-red-300 border-red-700/50 text-xs">
                      {flag}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
          
          {/* Toggle aperçu contenu */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowDetails(!showDetails)}
            className="text-slate-400 hover:text-white p-0 h-auto"
          >
            <Eye className="w-4 h-4 mr-1" />
            {showDetails ? 'Masquer' : 'Voir'} le contenu reçu
            {showDetails ? <ChevronUp className="w-4 h-4 ml-1" /> : <ChevronDown className="w-4 h-4 ml-1" />}
          </Button>
          
          {showDetails && (
            <div className="mt-3 bg-slate-900/50 border border-slate-700 rounded p-3 max-h-[200px] overflow-y-auto">
              <p className="text-xs text-slate-500 mb-2">
                {isSuggestion ? 'Email d\'origine :' : 'Aperçu des réponses reçues :'}
              </p>
              <pre className="text-xs text-slate-300 whitespace-pre-wrap font-mono">
                {isSuggestion
                  ? ((interview.proposal_body || '').slice(0, 1000) + ((interview.proposal_body || '').length > 1000 ? '...' : ''))
                  : (responsesPreview || '(Aucun contenu)')}
                {!isSuggestion && responsesPreview && responsesPreview.length >= 500 && '...'}
              </pre>
            </div>
          )}

          {/* Sélection des réponses pour génération */}
          {showResponseSelector && !isSuggestion && responses.length > 0 && (
            <div className="mt-4 bg-sky-950/30 border border-sky-800/40 rounded-lg p-4" data-testid="response-selector">
              <div className="flex items-center gap-2 mb-3">
                <FileText className="w-4 h-4 text-sky-400" />
                <span className="text-sm font-medium text-sky-300">
                  Sélectionnez les réponses à utiliser ({selectedResponses.length}/{responses.length})
                </span>
              </div>
              <div className="space-y-2 max-h-[300px] overflow-y-auto">
                {responses.map((response, index) => {
                  const analysis = response.analysis || {};
                  const isAnswer = analysis.response_type === 'answers' || analysis.has_answers;
                  const isSelected = selectedResponses.includes(index);
                  const hasAttachments = (response.attachments || []).length > 0;
                  
                  return (
                    <div 
                      key={index}
                      onClick={() => toggleResponseSelection(index)}
                      className={`p-3 rounded-lg border cursor-pointer transition-all ${
                        isSelected 
                          ? 'bg-sky-900/40 border-sky-500/60' 
                          : 'bg-slate-800/40 border-slate-700 hover:border-slate-600'
                      }`}
                      data-testid={`response-${index}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${
                          isSelected ? 'bg-sky-500 border-sky-500' : 'border-slate-500'
                        }`}>
                          {isSelected && <CheckCircle className="w-3 h-3 text-white" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-medium text-slate-200">
                              Réponse #{index + 1}
                            </span>
                            {isAnswer && (
                              <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-500/40 text-xs">
                                Réponses détectées
                              </Badge>
                            )}
                            {hasAttachments && (
                              <Badge className="bg-purple-500/20 text-purple-300 border-purple-500/40 text-xs">
                                {response.attachments.length} pièce(s) jointe(s)
                              </Badge>
                            )}
                          </div>
                          <div className="text-xs text-slate-400 mt-1">
                            {response.received_at ? new Date(response.received_at).toLocaleString('fr-FR') : ''}
                            {response.from && ` • ${response.from.split('<')[0].trim()}`}
                          </div>
                          <div className="text-xs text-slate-500 mt-2 line-clamp-2">
                            {(response.body || '').slice(0, 150)}{(response.body || '').length > 150 ? '...' : ''}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Sélection du portrait */}
              {availableImages.length > 0 && (
                <div className="mt-4 pt-3 border-t border-slate-700">
                  <div className="flex items-center gap-2 mb-3">
                    <Image className="w-4 h-4 text-purple-400" />
                    <span className="text-sm font-medium text-purple-300">
                      Portrait ({availableImages.length} image{availableImages.length > 1 ? 's' : ''} disponible{availableImages.length > 1 ? 's' : ''})
                    </span>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                    {/* Option: Aucun portrait */}
                    <div
                      onClick={() => setSelectedPortrait(null)}
                      className={`p-2 rounded-lg border cursor-pointer transition-all flex flex-col items-center justify-center min-h-[80px] ${
                        selectedPortrait === null
                          ? 'bg-purple-900/40 border-purple-500/60'
                          : 'bg-slate-800/40 border-slate-700 hover:border-slate-600'
                      }`}
                    >
                      <XCircle className={`w-6 h-6 mb-1 ${selectedPortrait === null ? 'text-purple-400' : 'text-slate-500'}`} />
                      <span className="text-xs text-slate-400">Aucun</span>
                    </div>
                    {/* Images disponibles */}
                    {availableImages.map((img) => (
                      <div
                        key={img.key}
                        onClick={() => setSelectedPortrait(img.key)}
                        className={`p-2 rounded-lg border cursor-pointer transition-all ${
                          selectedPortrait === img.key
                            ? 'bg-purple-900/40 border-purple-500/60 ring-2 ring-purple-500/50'
                            : 'bg-slate-800/40 border-slate-700 hover:border-slate-600'
                        }`}
                        data-testid={`portrait-${img.key}`}
                      >
                        <div className="aspect-square bg-slate-900 rounded overflow-hidden mb-1 flex items-center justify-center">
                          <img 
                            src={`${API_URL}/api/interviews/${interview.id}/attachment/${img.responseIndex}/${img.attachmentIndex}`}
                            alt={img.filename}
                            className="max-w-full max-h-full object-contain"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }}
                          />
                          <div className="hidden items-center justify-center text-slate-500">
                            <Image className="w-6 h-6" />
                          </div>
                        </div>
                        <div className="text-xs text-slate-400 truncate text-center" title={img.filename}>
                          {img.filename.length > 12 ? img.filename.slice(0, 10) + '...' : img.filename}
                        </div>
                        <div className="text-xs text-slate-500 text-center">
                          Rép. #{img.responseIndex + 1}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex items-center justify-between mt-4 pt-3 border-t border-slate-700">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowResponseSelector(false)}
                  className="text-slate-400"
                >
                  Annuler
                </Button>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setSelectedResponses(responses.map((_, i) => i))}
                    className="border-slate-600 text-slate-300"
                  >
                    Tout sélectionner
                  </Button>
                  <Button
                    onClick={handleApprove}
                    disabled={loading || selectedResponses.length === 0}
                    size="sm"
                    className="bg-emerald-600 hover:bg-emerald-700"
                  >
                    {loading ? <RefreshCw className="w-4 h-4 animate-spin mr-1" /> : <ThumbsUp className="w-4 h-4 mr-1" />}
                    Générer ({selectedResponses.length})
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
        
        {/* Actions */}
        <div className="flex flex-col gap-2 flex-shrink-0">
          <Button
            onClick={handleApprove}
            disabled={loading || showResponseSelector}
            className={isSuggestion ? "bg-indigo-600 hover:bg-indigo-700 text-white" : "bg-emerald-600 hover:bg-emerald-700 text-white"}
            data-testid={`approve-${interview.id}`}
          >
            {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <ThumbsUp className="w-4 h-4 mr-1" />}
            {isSuggestion ? 'Envoyer les questions' : (responses.length > 1 ? 'Sélectionner & Approuver' : 'Approuver')}
          </Button>
          <Button
            onClick={handleReject}
            disabled={loading}
            variant="outline"
            className="border-red-500/50 text-red-400 hover:bg-red-500/10"
            data-testid={`reject-${interview.id}`}
          >
            {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <ThumbsDown className="w-4 h-4 mr-1" />}
            Rejeter
          </Button>
        </div>
      </div>
    </div>
  );
}

function InterviewCard({ interview, onRefresh, token }) {
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  // Modales pour les nouvelles actions
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [showEditQuestionsModal, setShowEditQuestionsModal] = useState(false);
  const [rescheduleDate, setRescheduleDate] = useState('');
  const [editedQuestions, setEditedQuestions] = useState([]);
  
  const status = STATUS_CONFIG[interview.status] || STATUS_CONFIG.new;
  const StatusIcon = status.icon;
  
  const handleResendQuestions = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/interviews/${interview.id}/resend-questions`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('Questions renvoyées');
        onRefresh();
      } else {
        toast.error('Erreur lors du renvoi');
      }
    } catch (e) {
      handleNetworkError(e, 'approbation');
    }
    setLoading(false);
  };
  
  const handleForceRelance = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/interviews/${interview.id}/force-relance`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('Relance envoyée');
        onRefresh();
      } else {
        toast.error('Erreur lors de la relance');
      }
    } catch (e) {
      handleNetworkError(e, 'approbation');
    }
    setLoading(false);
  };
  
  const handleRegenerateArticle = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/interviews/${interview.id}/regenerate-article`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('Régénération lancée');
        onRefresh();
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Erreur lors de la régénération');
      }
    } catch (e) {
      handleNetworkError(e, 'approbation');
    }
    setLoading(false);
  };
  
  const handleReprocess = async () => {
    if (!window.confirm("Retraiter cette interview comme si elle avait été approuvée ?")) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/interviews/${interview.id}/reprocess`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(data.message || 'Interview retraitée');
        onRefresh();
      } else {
        toast.error(data.detail || 'Erreur');
      }
    } catch (e) {
      handleNetworkError(e, 'approbation');
    }
    setLoading(false);
  };

  const handleTogglePause = async () => {
    const willPause = !interview.paused;
    const action = willPause ? 'METTRE EN PAUSE' : 'RÉACTIVER';
    if (!window.confirm(`${action} les échanges automatiques sur cette interview ?\n\n${willPause ? 'Aucune relance ne sera envoyée tant que vous ne réactivez pas.' : 'Les relances automatiques reprendront selon le planning.'}`)) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/interviews/${interview.id}/toggle-pause`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(data.paused ? 'Échanges suspendus' : 'Échanges réactivés');
        onRefresh();
      } else {
        toast.error(data.detail || 'Erreur');
      }
    } catch (e) {
      handleNetworkError(e, 'approbation');
    }
    setLoading(false);
  };

  const handleAbandon = async () => {
    const reason = window.prompt(
      "Abandonner définitivement cette interview ?\n\nMotif (optionnel) — sera tracé dans l'historique :",
      ""
    );
    if (reason === null) return; // user cancelled
    if (!window.confirm("Confirmer l'abandon ?\n\nL'interview passera en statut 'Abandonné' et toutes les relances seront stoppées. Action irréversible (sauf retraitement manuel).")) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/interviews/${interview.id}/abandon`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason })
      });
      const data = await res.json();
      if (res.ok) {
        toast.success('Interview abandonnée');
        onRefresh();
      } else {
        toast.error(data.detail || 'Erreur');
      }
    } catch (e) {
      handleNetworkError(e, 'approbation');
    }
    setLoading(false);
  };

  // === NOUVELLES ACTIONS ===
  
  // Répondre aux questions pratiques
  const handleRespondToQuestions = async () => {
    if (!window.confirm("Envoyer une réponse automatique aux questions pratiques de l'interlocuteur ?\n\nUne réponse sera générée par IA basée sur la politique éditoriale d'EconomieMatin.")) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/interviews/${interview.id}/respond-to-questions`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({})
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(data.message || 'Réponse envoyée');
        onRefresh();
      } else {
        toast.error(data.detail || 'Erreur lors de l\'envoi');
      }
    } catch (e) {
      handleNetworkError(e, 'approbation');
    }
    setLoading(false);
  };

  // Ouvrir le modal de reprogrammation de relance
  const openRescheduleModal = () => {
    // Préremplir avec la date actuelle de relance ou J+7
    const defaultDate = interview.next_relance_at 
      ? new Date(interview.next_relance_at).toISOString().slice(0, 16)
      : new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().slice(0, 16);
    setRescheduleDate(defaultDate);
    setShowRescheduleModal(true);
  };

  // Confirmer la reprogrammation
  const handleRescheduleRelance = async () => {
    if (!rescheduleDate) {
      toast.error('Veuillez sélectionner une date');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/interviews/${interview.id}/reschedule-relance`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ next_relance_at: new Date(rescheduleDate).toISOString() })
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(`Relance reprogrammée au ${new Date(data.next_relance_at).toLocaleString('fr-FR')}`);
        setShowRescheduleModal(false);
        onRefresh();
      } else {
        toast.error(data.detail || 'Erreur');
      }
    } catch (e) {
      handleNetworkError(e, 'approbation');
    }
    setLoading(false);
  };

  // Ouvrir le modal de modification des questions
  const openEditQuestionsModal = () => {
    setEditedQuestions(interview.questions || []);
    setShowEditQuestionsModal(true);
  };

  // Modifier une question dans le tableau
  const updateQuestion = (index, value) => {
    const newQuestions = [...editedQuestions];
    newQuestions[index] = value;
    setEditedQuestions(newQuestions);
  };

  // Ajouter une question
  const addQuestion = () => {
    if (editedQuestions.length < 15) {
      setEditedQuestions([...editedQuestions, '']);
    }
  };

  // Supprimer une question
  const removeQuestion = (index) => {
    if (editedQuestions.length > 3) {
      setEditedQuestions(editedQuestions.filter((_, i) => i !== index));
    }
  };

  // Sauvegarder les questions (sans envoyer)
  const handleSaveQuestions = async (sendImmediately = false) => {
    const cleanQuestions = editedQuestions.filter(q => q.trim());
    if (cleanQuestions.length < 3) {
      toast.error('Au moins 3 questions sont requises');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/interviews/${interview.id}/questions`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ questions: cleanQuestions, send_immediately: sendImmediately })
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(data.message || 'Questions modifiées');
        setShowEditQuestionsModal(false);
        onRefresh();
      } else {
        toast.error(data.detail || 'Erreur');
      }
    } catch (e) {
      handleNetworkError(e, 'approbation');
    }
    setLoading(false);
  };

  // Statuts "actifs" sur lesquels la pause a du sens
  const ACTIVE_STATUSES = ['new', 'questions_sent', 'response_pending', 'response_received', 'article_generated', 'pending_review'];
  const isActive = ACTIVE_STATUSES.includes(interview.status);
  
  return (
    <Card className="bg-slate-800/50 border-slate-700 hover:border-sky-500/30 transition-all">
      <CardContent className="p-4">
        {/* Header */}
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <Badge className={`${status.color} text-white text-xs`}>
                <StatusIcon className="w-3 h-3 mr-1" />
                {status.label}
              </Badge>
              {interview.relance_count > 0 && (
                <Badge variant="outline" className="text-orange-400 border-orange-400/50 text-xs">
                  {interview.relance_count} relance(s)
                </Badge>
              )}
              {interview.paused && (
                <Badge className="bg-amber-500/20 text-amber-300 border border-amber-500/50 text-xs" data-testid={`paused-badge-${interview.id}`}>
                  <Pause className="w-3 h-3 mr-1" />
                  Échanges en pause
                </Badge>
              )}
            </div>
            <h3 className="text-white font-medium truncate" title={interview.subject}>
              {interview.subject}
            </h3>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            {isActive && (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleTogglePause}
                  disabled={loading}
                  className={interview.paused
                    ? 'text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10'
                    : 'text-amber-400 hover:text-amber-300 hover:bg-amber-500/10'}
                  data-testid={`toggle-pause-${interview.id}`}
                  title={interview.paused
                    ? `Échanges en pause depuis ${interview.paused_at ? new Date(interview.paused_at).toLocaleString('fr-FR') : ''}${interview.paused_by ? ' par ' + interview.paused_by : ''} — Cliquer pour reprendre`
                    : 'Mettre en pause les relances automatiques'}
                >
                  {interview.paused
                    ? <><Play className="w-4 h-4 mr-1" /><span className="hidden md:inline">Reprendre</span></>
                    : <><Pause className="w-4 h-4 mr-1" /><span className="hidden md:inline">Pause</span></>}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleAbandon}
                  disabled={loading}
                  className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                  data-testid={`abandon-${interview.id}`}
                  title="Abandonner cette interview (stoppe toutes les relances)"
                >
                  <XCircle className="w-4 h-4 mr-1" />
                  <span className="hidden md:inline">Abandonner</span>
                </Button>
              </>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setExpanded(!expanded)}
              className="text-slate-400 hover:text-white"
              title={expanded ? 'Replier' : 'Voir les détails'}
            >
              {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </Button>
          </div>
        </div>
        
        {/* Info de base */}
        <div className="mt-3 space-y-1 text-sm text-slate-400">
          <div className="flex items-center gap-2">
            <User className="w-3 h-3" />
            <span className="truncate">
              {interview.original_sender?.name || 'Inconnu'} 
              <span className="text-slate-500 ml-1">({interview.original_sender?.email})</span>
            </span>
          </div>
          {interview.analysis?.company && (
            <div className="flex items-center gap-2">
              <Building className="w-3 h-3" />
              <span>{interview.analysis.company}</span>
            </div>
          )}
          <div className="flex items-center gap-2">
            <Calendar className="w-3 h-3" />
            <span>{new Date(interview.created_at).toLocaleDateString('fr-FR', { 
              day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
            })}</span>
          </div>
          {/* Motif d'abandon (visible toujours pour interviews abandonnés/rejetés) */}
          {['abandoned', 'redirected'].includes(interview.status) && interview.abandoned_reason && (
            <div className="mt-2 p-2 bg-red-900/20 border border-red-500/30 rounded text-xs text-red-300">
              <span className="font-semibold">Motif :</span> {interview.abandoned_reason}
            </div>
          )}
          {/* Sites cibles si présents */}
          {interview.target_sites?.sites?.length > 0 && (
            <div className="mt-1 flex flex-wrap gap-1.5 items-center">
              <span className="text-[10px] text-slate-500 uppercase">Sites :</span>
              {interview.target_sites.sites.map((s) => (
                <Badge
                  key={s}
                  variant="outline"
                  className={`text-[10px] ${s === interview.target_sites.canonical 
                    ? 'border-emerald-500/50 text-emerald-300' 
                    : 'border-slate-600 text-slate-400'}`}
                >
                  {s}{s === interview.target_sites.canonical ? ' ★' : ''}
                </Badge>
              ))}
            </div>
          )}
        </div>
        
        {/* Détails expandés */}
        {expanded && (
          <div className="mt-4 pt-4 border-t border-slate-700 space-y-4">
            {/* Analyse */}
            {interview.analysis && (
              <div className="bg-slate-900/50 rounded-lg p-3">
                <h4 className="text-sky-400 text-sm font-medium mb-2">Analyse</h4>
                <p className="text-slate-300 text-sm">{interview.analysis.topic}</p>
                {interview.analysis.person_to_interview && (
                  <p className="text-slate-400 text-xs mt-1">
                    À interviewer : {interview.analysis.person_to_interview}
                  </p>
                )}
                {interview.analysis.key_angles?.length > 0 && (
                  <div className="mt-2">
                    <p className="text-slate-500 text-xs">Angles suggérés :</p>
                    <ul className="text-slate-400 text-xs list-disc list-inside">
                      {interview.analysis.key_angles.slice(0, 3).map((angle, i) => (
                        <li key={`angle-${i}-${angle.slice(0, 15)}`}>{angle}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
            
            {/* Questions */}
            {interview.questions?.length > 0 && (
              <div className="bg-slate-900/50 rounded-lg p-3">
                <h4 className="text-sky-400 text-sm font-medium mb-2">
                  Questions ({interview.questions.length})
                </h4>
                <ol className="text-slate-300 text-sm space-y-1 list-decimal list-inside">
                  {interview.questions.map((q, i) => (
                    <li key={`q-${i}-${q.slice(0, 15)}`} className="truncate" title={q}>{q}</li>
                  ))}
                </ol>
              </div>
            )}
            
            {/* Réponses */}
            {interview.responses?.length > 0 && (
              <div className="bg-slate-900/50 rounded-lg p-3">
                <h4 className="text-sky-400 text-sm font-medium mb-2">
                  Réponses ({interview.responses.length})
                </h4>
                {interview.responses.map((r, i) => (
                  <div key={`resp-${i}-${r.received_at}`} className="text-sm mb-2">
                    <p className="text-slate-500 text-xs">
                      {new Date(r.received_at).toLocaleString('fr-FR')}
                    </p>
                    <p className="text-slate-300 line-clamp-3">{r.body?.slice(0, 300)}...</p>
                    {r.attachments?.length > 0 && (
                      <p className="text-sky-400 text-xs mt-1">
                        {r.attachments.length} pièce(s) jointe(s)
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
            
            {/* WordPress */}
            {interview.wordpress_edit_url && (
              <a 
                href={interview.wordpress_edit_url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-sky-400 hover:text-sky-300 text-sm"
              >
                <ExternalLink className="w-4 h-4" />
                Voir dans WordPress
              </a>
            )}
            
            {/* Actions */}
            <div className="flex flex-wrap gap-2 pt-2">
              {['questions_sent', 'response_pending'].includes(interview.status) && (
                <>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={handleResendQuestions}
                    disabled={loading || interview.paused}
                    className="text-sky-400 border-sky-400/50 hover:bg-sky-400/10 disabled:opacity-40"
                    data-testid={`resend-questions-${interview.id}`}
                  >
                    <Send className="w-3 h-3 mr-1" />
                    Renvoyer questions
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={handleForceRelance}
                    disabled={loading || interview.paused}
                    className="text-orange-400 border-orange-400/50 hover:bg-orange-400/10 disabled:opacity-40"
                    data-testid={`force-relance-${interview.id}`}
                  >
                    <Clock className="w-3 h-3 mr-1" />
                    Relancer
                  </Button>
                </>
              )}
              {/* Reprogrammer relance - disponible pour statuts actifs */}
              {['questions_sent', 'response_pending', 'response_received', 'needs_human_review'].includes(interview.status) && (
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={openRescheduleModal}
                  disabled={loading}
                  className="text-indigo-400 border-indigo-400/50 hover:bg-indigo-400/10"
                  data-testid={`reschedule-relance-${interview.id}`}
                >
                  <CalendarClock className="w-3 h-3 mr-1" />
                  Reprogrammer
                </Button>
              )}
              {/* Répondre aux questions - si on a reçu une réponse */}
              {interview.responses?.length > 0 && ['response_received', 'needs_human_review', 'response_pending'].includes(interview.status) && (
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={handleRespondToQuestions}
                  disabled={loading}
                  className="text-teal-400 border-teal-400/50 hover:bg-teal-400/10"
                  data-testid={`respond-questions-${interview.id}`}
                >
                  <Reply className="w-3 h-3 mr-1" />
                  Répondre aux questions
                </Button>
              )}
              {/* Modifier les questions - disponible si questions existent */}
              {interview.questions?.length > 0 && !['published', 'redirected', 'abandoned'].includes(interview.status) && (
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={openEditQuestionsModal}
                  disabled={loading}
                  className="text-amber-400 border-amber-400/50 hover:bg-amber-400/10"
                  data-testid={`edit-questions-${interview.id}`}
                >
                  <Edit3 className="w-3 h-3 mr-1" />
                  Modifier questions
                </Button>
              )}
              {interview.responses?.length > 0 && !['published', 'redirected', 'abandoned'].includes(interview.status) && (
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={handleRegenerateArticle}
                  disabled={loading}
                  className="text-purple-400 border-purple-400/50 hover:bg-purple-400/10"
                  data-testid={`regenerate-article-${interview.id}`}
                >
                  <FileText className="w-3 h-3 mr-1" />
                  Régénérer article
                </Button>
              )}
              {['redirected', 'abandoned'].includes(interview.status) && (
                <Button 
                  size="sm"
                  onClick={handleReprocess}
                  disabled={loading}
                  className="bg-emerald-600 hover:bg-emerald-500 text-white"
                  data-testid={`reprocess-${interview.id}`}
                >
                  <RotateCcw className="w-3 h-3 mr-1" />
                  Retraiter comme approuvée
                </Button>
              )}
            </div>
          </div>
        )}
      </CardContent>
      
      {/* Modales - en dehors de CardContent pour meilleur z-index */}
      {showRescheduleModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50" onClick={() => setShowRescheduleModal(false)}>
          <div className="bg-slate-800 rounded-lg p-6 max-w-md w-full mx-4 border border-slate-600" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-white mb-4">Reprogrammer la relance</h3>
            <p className="text-slate-400 text-sm mb-4">
              Choisissez la nouvelle date et heure pour la prochaine relance automatique.
            </p>
            <input
              type="datetime-local"
              value={rescheduleDate}
              onChange={(e) => setRescheduleDate(e.target.value)}
              className="w-full bg-slate-700 border border-slate-600 rounded px-3 py-2 text-white mb-4"
              data-testid="reschedule-date-input"
            />
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setShowRescheduleModal(false)} className="text-slate-400">
                Annuler
              </Button>
              <Button 
                onClick={handleRescheduleRelance} 
                disabled={loading}
                className="bg-indigo-600 hover:bg-indigo-500"
                data-testid="confirm-reschedule-btn"
              >
                {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Confirmer'}
              </Button>
            </div>
          </div>
        </div>
      )}
      
      {showEditQuestionsModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50" onClick={() => setShowEditQuestionsModal(false)}>
          <div className="bg-slate-800 rounded-lg p-6 max-w-2xl w-full mx-4 border border-slate-600 max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-white mb-4">Modifier les questions</h3>
            <p className="text-slate-400 text-sm mb-4">
              Modifiez les questions avant de les envoyer. Minimum 3, maximum 15 questions.
            </p>
            <div className="space-y-2 mb-4">
              {editedQuestions.map((q, index) => (
                <div key={`question-${index}-${q.slice(0, 10)}`} className="flex gap-2">
                  <span className="text-slate-500 text-sm w-6 pt-2">{index + 1}.</span>
                  <input
                    type="text"
                    value={q}
                    onChange={(e) => updateQuestion(index, e.target.value)}
                    className="flex-1 bg-slate-700 border border-slate-600 rounded px-3 py-2 text-white text-sm"
                    placeholder={`Question ${index + 1}`}
                    data-testid={`edit-question-${index}`}
                  />
                  {editedQuestions.length > 3 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeQuestion(index)}
                      className="text-red-400 hover:text-red-300 px-2"
                    >
                      <XCircle className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
            {editedQuestions.length < 15 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={addQuestion}
                className="text-sky-400 hover:text-sky-300 mb-4"
              >
                + Ajouter une question
              </Button>
            )}
            <div className="flex justify-end gap-2 pt-4 border-t border-slate-700">
              <Button variant="ghost" onClick={() => setShowEditQuestionsModal(false)} className="text-slate-400">
                Annuler
              </Button>
              <Button 
                onClick={() => handleSaveQuestions(false)} 
                disabled={loading}
                variant="outline"
                className="border-amber-400/50 text-amber-400"
                data-testid="save-questions-btn"
              >
                Sauvegarder
              </Button>
              <Button 
                onClick={() => handleSaveQuestions(true)} 
                disabled={loading}
                className="bg-emerald-600 hover:bg-emerald-500"
                data-testid="save-and-send-questions-btn"
              >
                {loading ? <RefreshCw className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />}
                Sauvegarder & Envoyer
              </Button>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
}

export default function InterviewsPage() {
  const [interviews, setInterviews] = useState([]);
  const [needsReview, setNeedsReview] = useState([]);
  const [stats, setStats] = useState(null);
  const [tracking, setTracking] = useState(null);
  const [serviceStatus, setServiceStatus] = useState(null);
  const [togglingService, setTogglingService] = useState(false);
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  // Nouvelles fonctionnalités: pagination et messages inbound
  const [offset, setOffset] = useState(0);
  const [inboundMessages, setInboundMessages] = useState([]);
  const [showInbound, setShowInbound] = useState(false);
  const token = localStorage.getItem('auth_token');

  const fetchMe = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/auth/me`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) setMe(await res.json());
    } catch (e) { 
      // Non-critical: user info fetch failed
    }
  }, [token]);

  const fetchNeedsReview = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/interviews/needs-review`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) setNeedsReview(await res.json());
    } catch (e) { 
      // Non-critical: needs review fetch failed
    }
  }, [token]);

  const fetchTracking = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/interviews/tracking/summary`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) setTracking(await res.json());
    } catch (e) { /* ignore */ }
  }, [token]);

  const fetchServiceStatus = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/interview-service/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) setServiceStatus(await res.json());
    } catch (e) { /* ignore */ }
  }, [token]);

  const handleToggleService = async () => {
    if (!serviceStatus) return;
    const newEnabled = !serviceStatus.enabled;
    const action = newEnabled ? 'RÉACTIVER' : 'DÉSACTIVER';
    if (!window.confirm(`${action} le service Auto-Interviews ?\n\nCela ${newEnabled ? 'réactive' : 'suspend'} le polling IMAP, les relances automatiques et le scheduler hebdo.`)) return;
    setTogglingService(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/interview-service/toggle`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled: newEnabled })
      });
      if (res.ok) {
        toast.success(newEnabled ? 'Service Auto-Interviews réactivé' : 'Service Auto-Interviews désactivé');
        fetchServiceStatus();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Échec du toggle');
      }
    } catch (e) {
      handleNetworkError(e, 'approbation');
    } finally {
      setTogglingService(false);
    }
  };

  const fetchInterviews = useCallback(async () => {
    try {
      let url;
      if (filter === 'all') {
        url = `${API_URL}/api/interviews?limit=30&offset=${offset}`;
      } else if (filter === 'rejected') {
        url = `${API_URL}/api/interviews/rejected?limit=30&offset=${offset}`;
      } else if (filter === 'needs_human_review') {
        url = `${API_URL}/api/interviews/needs-review?limit=30&offset=${offset}`;
      } else {
        url = `${API_URL}/api/interviews?status=${filter}&limit=30&offset=${offset}`;
      }
      
      const res = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setInterviews(data);
      }
    } catch (e) {
      console.error('Erreur chargement interviews:', e);
    }
    setLoading(false);
  }, [token, filter, offset]);

  // Nouvelle fonction: récupérer les messages inbound en attente de rattachement
  const fetchInbound = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/interviews/inbound-review`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setInboundMessages(data);
      }
    } catch (e) {
      console.debug('Inbound review not available:', e);
    }
  }, [token]);

  // Nouvelle fonction: rattacher un message à un dossier
  const attachInboundMessage = async (messageId, interviewId) => {
    try {
      const res = await fetch(`${API_URL}/api/interviews/inbound-attach`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ message_id: messageId, interview_id: interviewId })
      });
      if (res.ok) {
        toast.success('Message rattaché avec succès');
        fetchInbound();
        fetchInterviews();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Erreur lors du rattachement');
      }
    } catch (e) {
      toast.error('Erreur réseau');
    }
  };
  
  const fetchStats = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/interviews/stats/summary`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setStats(data);
      }
    } catch (e) {
      console.error('Erreur chargement stats:', e);
    }
  }, [token]);
  
  useEffect(() => {
    fetchInterviews();
    fetchStats();
    fetchTracking();
    fetchServiceStatus();
    fetchMe();
    fetchNeedsReview();
    fetchInbound();
  }, [fetchInterviews, fetchStats, fetchTracking, fetchServiceStatus, fetchMe, fetchNeedsReview, fetchInbound]);

  // Reset offset quand le filtre change
  useEffect(() => {
    setOffset(0);
  }, [filter]);
  
  const handleCheckNow = async () => {
    try {
      const res = await fetch(`${API_URL}/api/interviews/check-now`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('Vérification des emails lancée');
        setTimeout(() => {
          fetchInterviews();
          fetchStats();
        }, 5000);
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Erreur (service peut-être désactivé)');
      }
    } catch (e) {
      toast.error('Erreur');
    }
  };
  
  const handleRefresh = () => {
    fetchInterviews();
    fetchStats();
    fetchTracking();
    fetchServiceStatus();
    fetchNeedsReview();
    fetchInbound();
  };
  
  const isAmiral = me?.grade === 'amiral';
  const serviceOn = serviceStatus?.enabled !== false; // default true tant qu'on n'a pas fetch

  return (
    <div className="space-y-6" data-testid="interviews-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2 flex-wrap">
            <MessageSquare className="w-6 h-6 text-sky-400" />
            Interviews
            {serviceStatus && (
              <Badge className={serviceOn ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40' : 'bg-red-500/20 text-red-300 border-red-500/40'}>
                {serviceOn ? <Power className="w-3 h-3 mr-1" /> : <PowerOff className="w-3 h-3 mr-1" />}
                {serviceOn ? 'Service actif' : 'Service désactivé'}
              </Badge>
            )}
          </h1>
          <p className="text-slate-400 mt-1">
            Gestion des interviews par email (interviews@economiematin.fr)
          </p>
        </div>
        <div className="flex gap-2 flex-wrap items-center">
          {/* Toggle LLM Mode */}
          <ServiceLLMToggle serviceId="interviews" />
          
          {isAmiral && serviceStatus && (
            <Button
              variant="outline"
              onClick={handleToggleService}
              disabled={togglingService}
              className={serviceOn
                ? 'border-red-500/50 text-red-400 hover:bg-red-500/10'
                : 'border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10'}
              data-testid="btn-toggle-interview-service"
            >
              {togglingService ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : (serviceOn ? <PowerOff className="w-4 h-4 mr-2" /> : <Power className="w-4 h-4 mr-2" />)}
              {serviceOn ? 'Désactiver le service' : 'Activer le service'}
            </Button>
          )}
          <Button 
            variant="outline" 
            onClick={handleRefresh}
            className="border-slate-600 text-slate-300 hover:bg-slate-700"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Actualiser
          </Button>
          <Button 
            onClick={handleCheckNow}
            disabled={!serviceOn}
            className="bg-sky-600 hover:bg-sky-500 text-white font-semibold disabled:opacity-40"
          >
            <Mail className="w-4 h-4 mr-2" />
            Vérifier maintenant
          </Button>
        </div>
      </div>

      {/* Mission Auto-Interviews + Tracking par rédacteur */}
      {tracking && (
        <Card className="bg-gradient-to-br from-sky-950/30 to-slate-900/40 border-sky-800/40" data-testid="tracking-section">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg text-sky-400 flex items-center gap-2">
              <TrendingUp className="w-5 h-5" />
              Mission Auto-Interviews — Suivi rédacteurs
            </CardTitle>
            <p className="text-xs text-slate-400 mt-1">
              Objectif : <strong className="text-amber-300">1 auto-interview lancée par jour ouvré et par rédacteur</strong> (~20 par mois).
              {' '}Relances automatiques aux inactifs chaque mardi et vendredi à 17h.
            </p>
          </CardHeader>
          <CardContent>
            {/* KPI mini */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
              <div className="bg-sky-900/30 border border-sky-800/50 rounded-lg p-3 text-center">
                <div className="text-[10px] uppercase tracking-wider text-sky-400">Lancées 7j</div>
                <div className="text-2xl font-bold text-sky-200 mt-1">{tracking.total_started_7j}</div>
              </div>
              <div className="bg-emerald-900/30 border border-emerald-800/50 rounded-lg p-3 text-center">
                <div className="text-[10px] uppercase tracking-wider text-emerald-400">Publiées 7j</div>
                <div className="text-2xl font-bold text-emerald-200 mt-1">{tracking.total_published_7j}</div>
              </div>
              <div className="bg-slate-900/40 border border-slate-700 rounded-lg p-3 text-center">
                <div className="text-[10px] uppercase tracking-wider text-slate-400">Lancées 30j</div>
                <div className="text-2xl font-bold text-slate-200 mt-1">{tracking.total_started_30j}</div>
              </div>
              <div className="bg-slate-900/40 border border-slate-700 rounded-lg p-3 text-center">
                <div className="text-[10px] uppercase tracking-wider text-slate-400">Publiées 30j</div>
                <div className="text-2xl font-bold text-slate-200 mt-1">{tracking.total_published_30j}</div>
              </div>
              <div className={`${tracking.inactifs_count > 0 ? 'bg-red-900/30 border-red-800/50' : 'bg-emerald-900/30 border-emerald-800/50'} border rounded-lg p-3 text-center`}>
                <div className={`text-[10px] uppercase tracking-wider ${tracking.inactifs_count > 0 ? 'text-red-400' : 'text-emerald-400'}`}>Inactifs 7j</div>
                <div className={`text-2xl font-bold mt-1 ${tracking.inactifs_count > 0 ? 'text-red-300' : 'text-emerald-200'}`}>
                  {tracking.inactifs_count} / {tracking.count}
                </div>
              </div>
            </div>

            {/* Table par rédacteur */}
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-slate-400 uppercase text-[10px] tracking-wider border-b border-slate-800">
                    <th className="text-left py-2 px-2">Rédacteur</th>
                    <th className="text-center py-2 px-2">Lancées 7j</th>
                    <th className="text-center py-2 px-2">Publiées 7j</th>
                    <th className="text-center py-2 px-2">Lancées 30j</th>
                    <th className="text-center py-2 px-2">Publiées 30j</th>
                    <th className="text-center py-2 px-2">Dernier lancement</th>
                  </tr>
                </thead>
                <tbody>
                  {tracking.stats.map((s) => {
                    const isMe = me && (s.email === (me.email || '').toLowerCase());
                    return (
                      <tr key={s.email} className={`border-b border-slate-900 ${s.needs_reminder ? 'bg-red-950/10' : 'bg-emerald-950/5'} ${isMe ? 'ring-1 ring-sky-500/30' : ''}`} data-testid={`tracking-row-${s.email}`}>
                        <td className="py-2 px-2">
                          <div className="font-semibold text-slate-100 flex items-center gap-2 flex-wrap">
                            {s.name}
                            {isMe && <Badge className="bg-sky-500/20 text-sky-300 border-sky-500/40 text-[10px]">Moi</Badge>}
                          </div>
                          <div className="text-[11px] text-slate-500">{s.email} · {s.grade}</div>
                        </td>
                        <td className={`text-center py-2 px-2 font-bold ${s.started_7j === 0 ? 'text-red-400' : 'text-emerald-400'}`}>
                          {s.started_7j}
                        </td>
                        <td className="text-center py-2 px-2 text-slate-300">{s.published_7j}</td>
                        <td className="text-center py-2 px-2 text-slate-300">{s.started_30j}</td>
                        <td className="text-center py-2 px-2 text-slate-300">{s.published_30j}</td>
                        <td className="text-center py-2 px-2 text-[11px] text-slate-500">
                          {s.last_started_at ? new Date(s.last_started_at).toLocaleDateString('fr-FR') : '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-white">{stats.total}</p>
              <p className="text-slate-400 text-sm">Total</p>
            </CardContent>
          </Card>
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-yellow-400">{stats.pending_responses}</p>
              <p className="text-slate-400 text-sm">En attente</p>
            </CardContent>
          </Card>
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-sky-400">{stats.ready_for_review}</p>
              <p className="text-slate-400 text-sm">À valider</p>
            </CardContent>
          </Card>
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-emerald-400">{stats.published}</p>
              <p className="text-slate-400 text-sm">Publiés</p>
            </CardContent>
          </Card>
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-gray-400">{stats.by_status?.redirected || 0}</p>
              <p className="text-slate-400 text-sm">Redirigés</p>
            </CardContent>
          </Card>
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-red-400">{stats.by_status?.abandoned || 0}</p>
              <p className="text-slate-400 text-sm">Abandonnés</p>
            </CardContent>
          </Card>
        </div>
      )}
      
      {/* Section Arbitrage - Interviews nécessitant une validation humaine */}
      {needsReview.length > 0 && (
        <Card className="bg-gradient-to-br from-amber-950/40 to-slate-900/60 border-amber-700/50" data-testid="needs-review-section">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg text-amber-400 flex items-center gap-2">
              <ShieldAlert className="w-5 h-5" />
              Arbitrage requis ({needsReview.length})
              <Badge className="bg-amber-500/20 text-amber-300 border-amber-500/40 ml-2">Action requise</Badge>
            </CardTitle>
            <p className="text-xs text-slate-400 mt-1">
              Ces interviews ont été bloquées car le contenu reçu ne semble pas être une vraie interview. 
              Vérifiez et décidez de générer l&apos;article ou de rejeter.
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            {needsReview.map(interview => (
              <ArbitrageCard 
                key={interview.id} 
                interview={interview} 
                token={token}
                onRefresh={handleRefresh}
              />
            ))}
          </CardContent>
        </Card>
      )}
      
      {/* Filtres */}
      <div className="flex flex-wrap gap-2">
        {[
          { value: 'all', label: 'Tous' },
          { value: 'needs_human_review', label: '⚠️ Arbitrage', count: needsReview.length },
          { value: 'questions_sent', label: 'Questions envoyées' },
          { value: 'response_pending', label: 'En attente' },
          { value: 'response_received', label: 'Réponse reçue' },
          { value: 'pending_review', label: 'À valider' },
          { value: 'published', label: 'Publiés' },
          { value: 'redirected', label: 'Redirigés' },
          { value: 'abandoned', label: 'Abandonnés' },
          { value: 'rejected', label: '🔴 À retraiter' },
        ].map(f => (
          <Button
            key={f.value}
            variant={filter === f.value ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter(f.value)}
            className={filter === f.value 
              ? (f.value === 'rejected' ? 'bg-emerald-600 hover:bg-emerald-700' : f.value === 'needs_human_review' ? 'bg-amber-600 hover:bg-amber-700' : 'bg-sky-600 hover:bg-sky-500')
              : (f.value === 'needs_human_review' && f.count > 0 ? 'border-amber-500 text-amber-400 hover:bg-amber-500/10 animate-pulse' : 'border-slate-600 text-slate-300 hover:bg-slate-700')
            }
            data-testid={`filter-${f.value}`}
          >
            {f.label}
            {f.count > 0 && <Badge className="ml-2 bg-amber-500 text-white text-xs">{f.count}</Badge>}
          </Button>
        ))}
      </div>

      {/* Messages inbound en attente de rattachement */}
      {inboundMessages.length > 0 && (
        <Card className="bg-gradient-to-br from-amber-950/30 to-slate-900/40 border-amber-800/40">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg text-amber-400 flex items-center gap-2 cursor-pointer" onClick={() => setShowInbound(!showInbound)}>
              <Inbox className="w-5 h-5" />
              Messages à rattacher ({inboundMessages.length})
              {showInbound ? <ChevronUp className="w-4 h-4 ml-auto" /> : <ChevronDown className="w-4 h-4 ml-auto" />}
            </CardTitle>
            <p className="text-xs text-slate-400">
              Ces emails n'ont pas pu être rattachés automatiquement à un dossier interview.
            </p>
          </CardHeader>
          {showInbound && (
            <CardContent className="space-y-3">
              {inboundMessages.map(msg => (
                <div key={msg.message_id} className="bg-slate-800/50 border border-slate-700 rounded-lg p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-slate-200 truncate">{msg.subject || '(Sans sujet)'}</div>
                      <div className="text-xs text-slate-400 mt-1">
                        De: {msg.from} • {msg.created_at ? new Date(msg.created_at).toLocaleString('fr-FR') : ''}
                      </div>
                      {msg.error && (
                        <div className="text-xs text-amber-400 mt-1">
                          <AlertTriangle className="w-3 h-3 inline mr-1" />
                          {msg.error}
                        </div>
                      )}
                    </div>
                    <div className="flex gap-2 items-center">
                      <select 
                        className="bg-slate-700 border border-slate-600 rounded px-2 py-1 text-xs text-slate-200"
                        onChange={(e) => {
                          if (e.target.value) attachInboundMessage(msg.message_id, e.target.value);
                        }}
                        defaultValue=""
                      >
                        <option value="">Rattacher à...</option>
                        {interviews.slice(0, 20).map(itw => (
                          <option key={itw.id} value={itw.id}>
                            {itw.subject?.slice(0, 40) || itw.id.slice(0, 8)}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              ))}
            </CardContent>
          )}
        </Card>
      )}
      
      {/* Liste */}
      {loading ? (
        <div className="text-center py-12">
          <RefreshCw className="w-8 h-8 text-sky-400 animate-spin mx-auto" />
          <p className="text-slate-400 mt-2">Chargement...</p>
        </div>
      ) : interviews.length === 0 ? (
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-12 text-center">
            <MessageSquare className="w-12 h-12 text-slate-600 mx-auto mb-4" />
            <p className="text-slate-400">Aucune interview trouvée</p>
            <p className="text-slate-500 text-sm mt-1">
              Les propositions envoyées à interviews@economiematin.fr apparaîtront ici
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="grid gap-4">
            {interviews.map(interview => (
              <InterviewCard 
                key={interview.id} 
                interview={interview} 
                onRefresh={handleRefresh}
                token={token}
              />
            ))}
          </div>
          
          {/* Pagination */}
          <div className="flex items-center justify-center gap-4 mt-6">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setOffset(Math.max(0, offset - 30))}
              disabled={offset === 0}
              className="border-slate-600 text-slate-300 hover:bg-slate-700 disabled:opacity-50"
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Précédent
            </Button>
            <span className="text-sm text-slate-400">
              {offset + 1} - {offset + interviews.length}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setOffset(offset + 30)}
              disabled={interviews.length < 30}
              className="border-slate-600 text-slate-300 hover:bg-slate-700 disabled:opacity-50"
            >
              Suivant
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
